"use client";

// THE PAGE WAITS FOR THE STORE IT READS.
//
// A tab holds only the store groups its pages have read
// (lib/route-store-groups.ts): a document load is seeded with its own page's,
// and a client navigation into a page that reads another group loads it
// through the poll (components/store-sync.tsx). For that moment the page's
// stores are EMPTY BECAUSE THEY ARE EARLY, and every page that reads them
// would say something false from them: the editor "That script doesn't exist",
// the Videos page no weeks at all — and a New week button that mints week 1
// beside the four about to arrive (DRAFT-JAY-CORTTY-1 was made in exactly that
// window, before the seed closed it), the feedback console "nothing yet".
//
// So the page is not rendered until its groups are held. ONE gate, in the root
// layout, around every page, read from the same table the seed and the load
// read: a page cannot forget to wait, and a page that reads nothing never does.
//
// ⚠️ NEVER ON A DOCUMENT LOAD. The seed carries the page's groups and is
// applied during <StoreSeed>'s render, above this, on the server and in the
// browser alike, so the HTML and the hydration render both pass straight
// through. The gate shows only after a client navigation, once per group per
// tab: a held group is never let go.

import { useState, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { getGroupsVersion, groupLoadFailed, holdsGroups, pollStoreNow, subscribeGroups } from "@/lib/store-cursor";
import { storeGroupsFor } from "@/lib/route-store-groups";

// ─── AND IT SAYS SO WHEN THE LOAD FAILS ──────────────────────────────────────
//
// "Loading…" for as long as the load is out. When the poll carrying it fails,
// the page says it did not load, with Try again (a poll now): never a
// "Loading…" that has quietly stopped meaning anything. The poll's interval
// asks again by itself too, and the gate reads "Loading…" while it does.

export function StoreGroupGate({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [retrying, setRetrying] = useState(false);
  useSyncExternalStore(subscribeGroups, getGroupsVersion, getGroupsVersion);
  if (holdsGroups(storeGroupsFor(pathname ?? "/"))) return <>{children}</>;
  const failed = groupLoadFailed() && !retrying;
  return (
    <div className="flex-1 bg-app min-h-screen p-6" data-testid={failed ? "store-group-failed" : "store-group-loading"}>
      {failed ? (
        <div role="alert" className="text-[13px] text-text-muted">
          This page did not load.{" "}
          <button
            type="button"
            data-testid="store-group-retry"
            onClick={async () => {
              setRetrying(true);
              try {
                await pollStoreNow();
              } finally {
                setRetrying(false);
              }
            }}
            className="text-text underline decoration-dotted decoration-line-strong underline-offset-4 hover:text-accent-ink hover:decoration-current"
          >
            Try again
          </button>
        </div>
      ) : (
        <div role="status" className="text-[13px] text-text-dim">Loading…</div>
      )}
    </div>
  );
}
